import BigNumber from "@/components/BigNumber";
import InfoTooltip from "@/components/InfoTooltip/InfoTooltip";
import Column from "@/components/Ui/Column";
import Icon from "@/components/Ui/Icon";
import Row from "@/components/Ui/Row";
import VendorLink from "@/components/VendorLink";
import { GraphToggleOption } from "@/utils/graphHelpers";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  title: string;
  description?: string;
  value?: number;
  unit?: string;
  gradient?: boolean;
  equalHeight?: boolean;
  queryUrl?: string;
  toggleOptions?: GraphToggleOption[];
  onGraphToggle?: (option: GraphToggleOption) => void;
};

const GraphHeader = ({
  title,
  description,
  value,
  unit,
  gradient = false,
  equalHeight = false,
  queryUrl,
  toggleOptions,
  onGraphToggle,
}: Props) => {
  const currentOption = useMemo(
    () => toggleOptions?.find((option) => option.current),
    [toggleOptions],
  );

  return (
    <Column left className="w-full gap-3">
      <Row
        left
        up
        className={twMerge(
          "relative w-full flex-nowrap justify-between gap-2",
          equalHeight && "h-14",
        )}
      >
        <div
          className={twMerge(
            "text-muted-foreground line-clamp-2 text-lg font-medium",
            gradient && "text-foreground/80",
          )}
        >
          {title}
        </div>
        <Row right up className="relative shrink-0 flex-nowrap gap-1.5">
          {queryUrl && (
            <VendorLink
              href={queryUrl}
              className="text-muted-foreground bg-primary/30 row hover:bg-primary/40 relative h-6 rounded-sm p-0 pr-[22px] pl-2 text-[12px] font-medium"
            >
              <span className="block translate-y-px">More Details</span>
              <Icon
                name="chevron-right"
                className="absolute top-1/2 right-[7px] -translate-y-1/2 text-[10px]"
              />
            </VendorLink>
          )}
          {toggleOptions && toggleOptions.length > 0 && (
            <div className="group relative h-6">
              <button className="text-muted-foreground bg-primary/30 group-hover:bg-primary/40 relative h-full rounded-sm p-0 pr-[22px] pl-2 text-[12px] font-medium">
                <span className="block translate-y-px">
                  {currentOption?.label}
                </span>
                <Icon
                  name="chevron-down"
                  className="absolute top-1/2 right-[7px] -translate-y-1/2 text-[10px]"
                />
              </button>
              <select
                onChange={(e) => {
                  const selected = toggleOptions?.find(
                    (option) => option.label === e.target.value,
                  );
                  if (selected && onGraphToggle) {
                    onGraphToggle(selected);
                  }
                }}
                className="absolute inset-0 cursor-pointer opacity-0"
              >
                {toggleOptions.map((option) => (
                  <option
                    key={option.label}
                    value={option.label}
                    selected={option.current}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {description && (
            <InfoTooltip className="ml-0 h-6" description={description} />
          )}
        </Row>
      </Row>
      {typeof value === "number" && (
        <Row
          className="text-(length:--headline-font-size,--spacing(10)) leading-[80%]"
          left
        >
          <BigNumber value={value} unit={unit} gradient={gradient} />
        </Row>
      )}
    </Column>
  );
};

export default GraphHeader;
